import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { AdminCreateRewardRequest, AdminRewardCatalog } from "@ternary/contracts"
import {
  Button,
  Checkbox,
  type Column,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Field,
  IconButton,
  Input,
  PageHeader,
  Table,
} from "@ternary/ui"
import { type FormEvent, useId, useRef, useState } from "react"
import { flushSync } from "react-dom"
import { createAdminReward, deleteAdminReward, loadAdminRewards } from "../api.js"
import { CommerceTabs, PaymentSettingsLink } from "../components/commerce-tabs.js"
import { DocsLink } from "../components/docs-link.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { NavIcon } from "../components/nav-icons.js"

type Reward = AdminRewardCatalog["rewards"][number]

function credit(amountMinor: number) {
  return new Intl.NumberFormat("en-US", {
    currency: "USD",
    style: "currency",
  }).format(amountMinor / 100)
}

type RewardErrors = { name?: string; referralCost?: string; creditAmount?: string }

/**
 * The credit amount field's error, or undefined. The field is the browser's own number field
 * (`min="0.01"`, `step=".01"`), so it takes what it always took (".5", "1e3", "25.500"), less
 * anything below a cent. The cents it sends must stay a whole number a request carries exactly.
 */
function creditAmountError(input: HTMLInputElement | null) {
  if (!input || input.value === "" || input.validity.rangeUnderflow) {
    // Under a cent but above 0 (0.005): the floor, not "above 0", which it already is.
    return Number(input?.value) > 0 ? "Enter at least 0.01." : "Enter an amount above 0."
  }
  if (input.validity.stepMismatch) return "Use at most 2 decimals, such as 25.50."
  if (!input.validity.valid) return "Enter an amount above 0."
  if (!Number.isSafeInteger(Math.round(Number(input.value) * 100))) return "Enter a smaller amount."
  return undefined
}

/**
 * The Create reward form. Its errors, the API's included, show inside the dialog, each under its
 * field: the form checks them itself, so every browser shows them the same way.
 */
function RewardForm({
  error,
  onClose,
  onSave,
  saving,
}: {
  error: string
  onClose(): void
  onSave(reward: AdminCreateRewardRequest): void
  saving: boolean
}) {
  const formId = useId()
  const formRef = useRef<HTMLFormElement>(null)
  const [name, setName] = useState("")
  const [referralCost, setReferralCost] = useState(1)
  const [applyCredits, setApplyCredits] = useState(true)
  // Empty until typed, with its example as the placeholder: a 0 here would be refused.
  const [creditAmount, setCreditAmount] = useState("")
  const [errors, setErrors] = useState<RewardErrors>({})
  const creditRef = useRef<HTMLInputElement>(null)

  function submit(event: FormEvent) {
    event.preventDefault()
    // What the browser's own checks took before (required, min and step), from a cent up.
    const found: RewardErrors = {}
    if (name === "") found.name = "Enter a name for the reward."
    if (!Number.isInteger(referralCost) || referralCost < 1) {
      found.referralCost = "Enter a whole number of at least 1."
    }
    const amount = creditAmount.trim()
    const amountError = applyCredits ? creditAmountError(creditRef.current) : undefined
    if (amountError) found.creditAmount = amountError
    flushSync(() => setErrors(found))
    if (Object.keys(found).length > 0) {
      formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
      return
    }
    onSave({
      creditAmountMinor: applyCredits ? Math.round(Number(amount) * 100) : null,
      name,
      referralCost,
    })
  }

  return (
    <Dialog
      description="Members earn it by referring others."
      footer={
        <>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button form={formId} loading={saving} type="submit">
            Create reward
          </Button>
        </>
      }
      onClose={onClose}
      open
      title="Create reward"
    >
      <form className="admin-stack" id={formId} noValidate onSubmit={submit} ref={formRef}>
        <Field error={errors.name} label="Reward" required>
          <Input
            onChange={(event) => {
              setName(event.target.value)
              setErrors(({ name: _edited, ...rest }) => rest)
            }}
            placeholder="Free strategy call"
            value={name}
          />
        </Field>
        <Field
          error={errors.referralCost}
          hint="How many people a member refers to earn it."
          label="Referrals needed"
          required
        >
          <Input
            min="1"
            onChange={(event) => {
              setReferralCost(Number(event.target.value))
              setErrors(({ referralCost: _edited, ...rest }) => rest)
            }}
            type="number"
            value={referralCost}
          />
        </Field>
        <Checkbox
          checked={applyCredits}
          hint="Added to the member's balance for their next payment."
          label="Give account credit"
          onChange={(event) => setApplyCredits(event.target.checked)}
        />
        {applyCredits ? (
          <Field error={errors.creditAmount} label="Credit amount" required>
            <Input
              min="0.01"
              onChange={(event) => {
                setCreditAmount(event.target.value)
                setErrors(({ creditAmount: _edited, ...rest }) => rest)
              }}
              placeholder="25"
              prefix="$"
              ref={creditRef}
              size="short"
              step=".01"
              type="number"
              value={creditAmount}
            />
          </Field>
        ) : null}
        {error ? (
          <p className="tny-error" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </Dialog>
  )
}

/**
 * The referral rewards. Create reward opens the create form; each row's Delete asks in a
 * ConfirmDialog first.
 */
export function AdminRewardsPage() {
  const queryClient = useQueryClient()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [showForm, setShowForm] = useState(false)
  const [deleting, setDeleting] = useState<Reward | null>(null)
  const [notice, setNotice] = useState("")
  const [actionError, setActionError] = useState("")
  const catalogQuery = useQuery({
    queryFn: loadAdminRewards,
    queryKey: ["admin-rewards"],
  })
  const catalog = catalogQuery.data
  const writable = Boolean(catalog?.writable)
  const create = useMutation({
    mutationFn: createAdminReward,
    async onSuccess(_data, reward) {
      setShowForm(false)
      setNotice(`${reward.name} created.`)
      await queryClient.invalidateQueries({ queryKey: ["admin-rewards"] })
    },
  })
  const remove = useMutation({
    mutationFn: (reward: Reward) => deleteAdminReward(reward.id),
    async onSuccess(_data, reward) {
      setDeleting(null)
      setNotice(`${reward.name} deleted.`)
      await queryClient.invalidateQueries({ queryKey: ["admin-rewards"] })
      // The row's Actions button left with the row, so focus goes to the page heading.
      headingRef.current?.focus()
    },
    onError(error) {
      setDeleting(null)
      setActionError(error instanceof Error ? error.message : "Reward could not be deleted.")
    },
  })

  function openForm() {
    setNotice("")
    setActionError("")
    create.reset()
    setShowForm(true)
  }

  // Disabled until the list has loaded, so it holds its place; a list that cannot load offers none.
  const addButton = (
    <Button disabled={!catalog || !writable} onClick={openForm}>
      Create reward
    </Button>
  )

  const columns: Column<Reward>[] = [
    { cell: (reward) => reward.name, header: "Reward", key: "name" },
    {
      align: "end",
      cell: (reward) =>
        reward.creditAmountMinor !== null ? credit(reward.creditAmountMinor) : "—",
      header: "Credit",
      key: "credit",
    },
    {
      align: "end",
      cell: (reward) => String(reward.referralCost),
      header: "Referrals needed",
      key: "referrals",
    },
    {
      align: "end",
      // The row's one action, so a button of its own rather than a menu of one.
      cell: (reward) => (
        <IconButton
          className="admin-row-delete"
          disabled={!writable}
          icon={<NavIcon name="trash" />}
          label={`Delete ${reward.name}`}
          onClick={(event) => {
            // Focused, so the ConfirmDialog returns there even where a click does not focus a
            // button (Safari).
            event.currentTarget.focus()
            setNotice("")
            setActionError("")
            setDeleting(reward)
          }}
        />
      ),
      header: "Actions",
      key: "actions",
    },
  ]

  return (
    <section className="admin-stack admin-page">
      <PageHeader
        actions={
          <>
            <DocsLink />
            <PaymentSettingsLink />
            {catalogQuery.isError || catalog?.rewards.length === 0 ? null : addButton}
          </>
        }
        description="What members earn for referring others."
        headingRef={headingRef}
        title="Rewards"
      />
      <CommerceTabs active="rewards" />
      {catalogQuery.isError ? (
        <LoadError
          detail={errorDetail(catalogQuery.error)}
          message="We couldn't load your rewards"
          onRetry={() => void catalogQuery.refetch()}
          retrying={catalogQuery.isFetching}
        />
      ) : (
        <>
          {actionError ? (
            <p className="tny-error" role="alert">
              {actionError}
            </p>
          ) : null}
          <p className="admin-note" role="status">
            {notice}
          </p>
          {catalog && catalog.rewards.length === 0 ? (
            <EmptyState
              action={writable ? addButton : undefined}
              description="A reward, such as account credit or a free month, gives members a reason to refer others."
              title="Create your first reward"
            />
          ) : (
            <Table
              caption="Rewards"
              captionHidden
              columns={columns}
              loading={catalogQuery.isPending}
              loadingLabel="Loading rewards…"
              rowKey={(reward) => reward.id}
              rows={catalog?.rewards ?? []}
            />
          )}
        </>
      )}
      {showForm ? (
        <RewardForm
          error={create.error instanceof Error ? create.error.message : ""}
          onClose={() => setShowForm(false)}
          onSave={(reward) => create.mutate(reward)}
          saving={create.isPending}
        />
      ) : null}
      <ConfirmDialog
        body="Members can no longer claim it. Rewards already claimed are kept."
        confirmLabel="Delete reward"
        danger
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting)
        }}
        open={deleting !== null}
        pending={remove.isPending}
        title={deleting ? `Delete “${deleting.name}”?` : "Delete this reward?"}
      />
    </section>
  )
}
